import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { uniqueBy } from 'lib/array/arrayUtils'
import { formatBytes } from 'lib/string/stringUtils'
import {
  ACCEPTED_FILE_FORMAT_LABELS,
  TOTAL_CHAT_FILE_SIZE_LIMIT_BYTES,
  exceedsChatFileSizeLimit,
  getChatFileKey,
  normaliseChatFile,
  partitionChatFiles,
} from '../chatFiles'

/** Adds picked, pasted or dropped files to a chat message, reporting the ones that can't be attached. */
export function useAddChatFiles(
  files: Array<File> | undefined,
  onFilesChange: ((files: Array<File>) => void) | undefined,
) {
  const intl = useIntl()

  return useCallback((selectedFiles: Array<File>) => {
    if (!onFilesChange || selectedFiles.length === 0) {
      return
    }

    const { validFiles, invalidFileNames } = partitionChatFiles(selectedFiles.map(normaliseChatFile))

    if (invalidFileNames.length > 0) {
      showSnackbar(
        intl.formatMessage(
          {
            id: 'MsYqYSByel_JeZ397Cjx',
            defaultMessage: '{count, plural, one {The following file cannot be uploaded} other {The following files cannot be uploaded}}: {fileNames}. Accepted formats: {formats}',
            description: 'Error message when files cannot be uploaded due to unsupported format',
          },
          {
            count: invalidFileNames.length,
            fileNames: intl.formatList(invalidFileNames, { type: 'conjunction' }),
            formats: intl.formatList(ACCEPTED_FILE_FORMAT_LABELS, { type: 'unit' }),
          },
        ),
        'critical',
      )
    }

    const attachedFiles = files ?? []
    const nextFiles = uniqueBy([...attachedFiles, ...validFiles], getChatFileKey)
    const addedCount = nextFiles.length - attachedFiles.length
    if (addedCount === 0) {
      return
    }

    // The budget is per message, so files already on the composer count towards it too.
    if (exceedsChatFileSizeLimit(nextFiles)) {
      showSnackbar(
        intl.formatMessage(
          {
            id: '1DyyAwcIdJkxBpk4Fqhq',
            defaultMessage: '{count, plural, one {This file is too large to attach} other {These files are too large to attach}}. The total size limit is {maxSize}.',
            description: 'Error message when the files chosen in the TripPlanner chat input exceed the total attachment size limit, worded for one file or several',
          },
          { count: addedCount, maxSize: formatBytes(TOTAL_CHAT_FILE_SIZE_LIMIT_BYTES) },
        ),
        'critical',
      )
      return
    }

    onFilesChange(nextFiles)
  }, [intl, files, onFilesChange])
}
